import React, { useState, useEffect, useCallback } from 'react';

// ==================== TYPES ====================
interface PlatformEngagement { likes: number; comments: number; shares: number; views: number; }
interface PlatformBreakdown { total: number; published: number; scheduled: number; failed: number; engagement: PlatformEngagement; }
interface TopPost { id: number; title?: string; content: string; status: string; target_platforms: string[]; engagement_data: Record<string, any>; engagementScore: number; created_at: string; media_urls?: string[]; }
interface RecentPost { id: number; title?: string; content: string; status: string; platforms: string[]; scheduled_for?: string; created_at: string; engagement_data: Record<string, any>; media_urls?: string[]; }
interface UpcomingPost { id: number; title?: string; content: string; platforms: string[]; scheduled_for: string; }
interface Account { id: number; platform: string; platform_name: string; platform_username: string; account_type: string; sync_status: string; platform_data: Record<string, any>; last_sync: string; created_at: string; }
interface AnalyticsData {
  accounts: Account[];
  stats: { totalPosts: number; publishedCount: number; scheduledCount: number; failedCount: number; draftCount: number; postsThisMonth: number; postsLastMonth: number; monthGrowth: number; };
  engagement: { totalLikes: number; totalComments: number; totalShares: number; totalViews: number; avgEngagementRate: number; };
  platformBreakdown: Record<string, PlatformBreakdown>;
  topPosts: TopPost[];
  topHashtags: { tag: string; count: number }[];
  recentPosts: RecentPost[];
  upcomingPosts: UpcomingPost[];
  activity: { date: string; created: number; published: number }[];
}

interface GoogleReview {
  reviewId?: string; name?: string; reviewer?: { displayName?: string; profilePhotoUrl?: string; };
  starRating?: string; comment?: string; createTime?: string; updateTime?: string;
  reviewReply?: { comment?: string; updateTime?: string; };
  accountId?: number; locationName?: string; businessName?: string;
}

interface GoogleInsights {
  queriesDirect: number; queriesIndirect: number; viewsMaps: number; viewsSearch: number;
  actionsWebsite: number; actionsPhone: number; actionsDrivingDirections: number;
  timeSeries: { date: string; metric: string; value: number }[];
  searchKeywords?: { keyword: string; impressions: number }[];
}

interface FBInsights {
  uniqueVisitors: number; postEngagements: number; pageViews: number; reactions: number;
  totalFans: number; totalFollowers: number;
  timeSeries: { date: string; metric: string; value: number }[];
}

interface IGInsights {
  impressions: number; reach: number; followerCount: number; profileViews: number;
  timeSeries: { date: string; metric: string; value: number }[];
}

// ==================== CONSTANTS ====================
const PLATFORM_CONFIG: Record<string, { name: string; icon: string; color: string; bg: string; border: string }> = {
  facebook: { name: 'Facebook', icon: '📘', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  instagram: { name: 'Instagram', icon: '📸', color: 'text-pink-700', bg: 'bg-pink-50', border: 'border-pink-200' },
  linkedin: { name: 'LinkedIn', icon: '💼', color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200' },
  google_my_business: { name: 'Google Business', icon: '📍', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  twitter: { name: 'Twitter/X', icon: '🐦', color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200' },
  tiktok: { name: 'TikTok', icon: '🎵', color: 'text-violet-700', bg: 'bg-violet-50', border: 'border-violet-200' },
};

const STATUS_BADGES: Record<string, { label: string; class: string }> = {
  published: { label: 'Publié', class: 'bg-green-100 text-green-800' },
  scheduled: { label: 'Programmé', class: 'bg-blue-100 text-blue-800' },
  draft: { label: 'Brouillon', class: 'bg-gray-100 text-gray-700' },
  failed: { label: 'Échoué', class: 'bg-red-100 text-red-800' },
  publishing: { label: 'Publication…', class: 'bg-yellow-100 text-yellow-800' },
};

const STAR_MAP: Record<string, number> = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };

// ==================== HELPERS ====================
function fmt(n: number | undefined | null): string {
  const v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1) + 'k';
  return v.toString();
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `il y a ${d}j`;
  return new Date(dateStr).toLocaleDateString('fr-FR');
}

function getToken(): string {
  return localStorage.getItem('authToken') || '';
}

function stars(rating: string | number): string {
  const n = typeof rating === 'number' ? rating : (STAR_MAP[rating] || 0);
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

// ==================== MINI CHARTS ====================
const MOIS_FR = ['janv.', 'fév.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
function dateFr(iso: string) {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${parseInt(d)} ${MOIS_FR[parseInt(m) - 1]}`;
}

function LineChart({ data, color = '#10b981', label = '' }: { data: { date: string; value: number }[]; color?: string; label?: string }) {
  if (!data || data.length < 2) return <div className="text-xs text-gray-400 text-center py-6">Pas assez de données</div>;

  const rawMax = Math.max(...data.map(d => d.value));
  const maxVal = Math.max(1, rawMax);
  const minVal = Math.min(...data.map(d => d.value));
  const total = data.reduce((s, d) => s + d.value, 0);
  const maxIdx = rawMax > 0 ? data.findIndex(d => d.value === rawMax) : 0;
  const w = 100;
  const h = 40;
  const pad = 3;
  const coords = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((d.value - minVal) / (maxVal - minVal || 1)) * (h - pad * 2) - pad;
    return { x, y };
  });
  const points = coords.map(c => `${c.x},${c.y}`).join(' ');
  const areaPoints = `0,${h} ${points} ${w},${h}`;
  const lastCoord = coords[coords.length - 1];
  const maxCoord = coords[maxIdx];

  return (
    <div className="relative">
      {label && <p className="text-[10px] text-gray-400 mb-1">{label}</p>}
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: '56px' }} preserveAspectRatio="none">
        <defs>
          <linearGradient id={`grad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill={`url(#grad-${color.replace('#','')})`} />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Dot on max value */}
        <circle cx={maxCoord.x} cy={maxCoord.y} r="1.8" fill="white" stroke={color} strokeWidth="1" />
        {/* Dot on last value */}
        <circle cx={lastCoord.x} cy={lastCoord.y} r="1.5" fill={color} />
      </svg>
      <div className="flex items-center justify-between mt-0.5">
        <span className="text-[10px] text-gray-400">{dateFr(data[0]?.date)}</span>
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ color, backgroundColor: color + '15' }}>
          Total : {fmt(total)}
        </span>
        <span className="text-[10px] text-gray-400">{dateFr(data[data.length - 1]?.date)}</span>
      </div>
    </div>
  );
}

/** Strip "(Translated by Google)" and everything after from review text */
function cleanComment(text: string | undefined): string {
  if (!text) return '';
  const idx = text.indexOf('(Translated by Google)');
  if (idx > 0) return text.substring(0, idx).trim();
  const idx2 = text.indexOf('(Traduit par Google)');
  if (idx2 > 0) return text.substring(0, idx2).trim();
  return text;
}

// ==================== MAIN COMPONENT ====================
export default function AnalyticsDashboard() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<RecentPost | TopPost | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Platform-specific data
  const [googleReviews, setGoogleReviews] = useState<GoogleReview[]>([]);
  const [googleInsights, setGoogleInsights] = useState<GoogleInsights | null>(null);
  const [fbInsights, setFbInsights] = useState<{ insights: FBInsights | null; posts: any[]; pageName: string }>({ insights: null, posts: [], pageName: '' });
  const [igInsights, setIgInsights] = useState<{ insights: IGInsights | null; media: any[]; accountName: string; followers: number }>({ insights: null, media: [], accountName: '', followers: 0 });
  const [fbComments, setFbComments] = useState<any[]>([]);
  const [igComments, setIgComments] = useState<any[]>([]);

  const [activeTab, setActiveTab] = useState<'overview' | 'google' | 'facebook' | 'instagram'>('overview');
  // Month filter for Google insights — default to previous month
  const getDefaultMonth = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
  const [googleMonth, setGoogleMonth] = useState(getDefaultMonth());
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [publishing, setPublishing] = useState<string | null>(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showAllKeywords, setShowAllKeywords] = useState(false);

  const fetchData = useCallback(async () => {
    const token = getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const overviewRes = await fetch('/api/analytics/overview', { headers });
      if (!overviewRes.ok) throw new Error('Erreur API');
      const overviewData = await overviewRes.json();
      setData(overviewData);

      const [reviewsRes, gInsightsRes, fbRes, igRes, fbCommRes, igCommRes] = await Promise.allSettled([
        fetch('/api/analytics/google/reviews', { headers }).then(r => r.json()),
        fetch(`/api/analytics/google/insights?month=${googleMonth}`, { headers }).then(r => r.json()),
        fetch('/api/analytics/facebook/insights', { headers }).then(r => r.json()),
        fetch('/api/analytics/instagram/insights', { headers }).then(r => r.json()),
        fetch('/api/analytics/facebook/comments', { headers }).then(r => r.json()),
        fetch('/api/analytics/instagram/comments', { headers }).then(r => r.json()),
      ]);

      if (reviewsRes.status === 'fulfilled' && reviewsRes.value.success) setGoogleReviews(reviewsRes.value.reviews || []);
      if (gInsightsRes.status === 'fulfilled' && gInsightsRes.value.success) setGoogleInsights(gInsightsRes.value.insights || null);
      if (fbRes.status === 'fulfilled' && fbRes.value.success) setFbInsights({ insights: fbRes.value.insights, posts: fbRes.value.posts || [], pageName: fbRes.value.pageName || '' });
      if (igRes.status === 'fulfilled' && igRes.value.success) setIgInsights({ insights: igRes.value.insights, media: igRes.value.media || [], accountName: igRes.value.accountName || '', followers: igRes.value.followers || 0 });
      if (fbCommRes.status === 'fulfilled' && fbCommRes.value.success) setFbComments(fbCommRes.value.comments || []);
      if (igCommRes.status === 'fulfilled' && igCommRes.value.success) setIgComments(igCommRes.value.comments || []);

      setLoading(false);
    } catch (err) {
      setError(String(err));
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refetch Google insights when month changes
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`/api/analytics/google/insights?month=${googleMonth}`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(res => { if (res.success) setGoogleInsights(res.insights || null); })
      .catch(() => {});
  }, [googleMonth]);

  const handleReply = async (reviewId: string, accountId: number) => {
    if (!replyText.trim()) return;
    try {
      const res = await fetch(`/api/analytics/google/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: replyText, accountId })
      });
      const json = await res.json();
      if (json.success) { setReplyingTo(null); setReplyText(''); fetchData(); }
      else alert('Erreur: ' + (json.error || 'Impossible de répondre'));
    } catch { alert('Erreur réseau'); }
  };

  const handleAutoPublish = async (review: GoogleReview) => {
    const reviewId = review.reviewId || review.name?.split('/').pop() || '';
    setPublishing(reviewId);
    try {
      const res = await fetch('/api/analytics/google/reviews/auto-publish', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewName: review.name, reviewerName: review.reviewer?.displayName || '', reviewText: cleanComment(review.comment) || '', starRating: review.starRating })
      });
      const json = await res.json();
      if (json.success) alert(`✅ ${json.message}`);
      else alert('Erreur: ' + (json.error || 'Publication impossible'));
    } catch { alert('Erreur réseau'); }
    finally { setPublishing(null); }
  };

  const handleFbReply = async (commentId: string, accountId: number) => {
    if (!replyText.trim()) return;
    try {
      const res = await fetch(`/api/analytics/facebook/comments/${commentId}/reply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: replyText, accountId })
      });
      const json = await res.json();
      if (json.success) { setReplyingTo(null); setReplyText(''); fetchData(); }
      else alert('Erreur: ' + (json.error || 'Impossible de répondre'));
    } catch { alert('Erreur réseau'); }
  };

  const handleIgReply = async (commentId: string, accountId: number) => {
    if (!replyText.trim()) return;
    try {
      const res = await fetch(`/api/analytics/instagram/comments/${commentId}/reply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: replyText, accountId })
      });
      const json = await res.json();
      if (json.success) { setReplyingTo(null); setReplyText(''); fetchData(); }
      else alert('Erreur: ' + (json.error || 'Impossible de répondre'));
    } catch { alert('Erreur réseau'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="animate-spin inline-block w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full mb-4" />
        <p className="text-gray-500 text-sm">Chargement du tableau de bord…</p>
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <span className="text-3xl mb-2 block">⚠️</span>
      <p className="text-red-700 font-medium">Erreur de chargement</p>
      <p className="text-red-600 text-sm mt-1">{error}</p>
    </div>
  );

  const { accounts, stats, engagement, platformBreakdown, topPosts, topHashtags, recentPosts, upcomingPosts, activity } = data;
  const publishedCurve = activity.map(a => ({ date: a.date, value: a.published }));
  const createdCurve = activity.map(a => ({ date: a.date, value: a.created }));

  const googleClicksCurve = googleInsights?.timeSeries?.filter(ts => ts.metric === 'WEBSITE_CLICKS').map(ts => ({ date: ts.date, value: ts.value })) || [];
  // Combine desktop+mobile search impressions into one curve, aggregate by date
  const googleViewsRaw = googleInsights?.timeSeries?.filter(ts => ts.metric === 'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH' || ts.metric === 'BUSINESS_IMPRESSIONS_MOBILE_SEARCH') || [];
  const googleViewsMap = new Map<string, number>();
  googleViewsRaw.forEach(ts => googleViewsMap.set(ts.date, (googleViewsMap.get(ts.date) || 0) + ts.value));
  const googleViewsCurve = Array.from(googleViewsMap.entries()).map(([date, value]) => ({ date, value })).sort((a, b) => a.date.localeCompare(b.date));
  // Calls curve
  const googleCallsCurve = googleInsights?.timeSeries?.filter(ts => ts.metric === 'CALL_CLICKS').map(ts => ({ date: ts.date, value: ts.value })) || [];
  const fbImpressionsCurve = fbInsights.insights?.timeSeries?.filter(ts => ts.metric === 'page_impressions').map(ts => ({ date: ts.date, value: ts.value })) || [];
  const fbEngagementCurve = fbInsights.insights?.timeSeries?.filter(ts => ts.metric === 'page_post_engagements').map(ts => ({ date: ts.date, value: ts.value })) || [];
  const igImpressionsCurve = igInsights.insights?.timeSeries?.filter(ts => ts.metric === 'impressions').map(ts => ({ date: ts.date, value: ts.value })) || [];
  const igReachCurve = igInsights.insights?.timeSeries?.filter(ts => ts.metric === 'reach').map(ts => ({ date: ts.date, value: ts.value })) || [];

  const hasGoogle = accounts.some(a => a.platform === 'google_my_business');
  const hasFacebook = accounts.some(a => a.platform === 'facebook');
  const hasInstagram = accounts.some(a => a.platform === 'instagram');

  return (
    <div className="space-y-6">

      {/* === TAB NAVIGATION === */}
      <div className="flex border-b border-gray-200 -mb-2">
        {[
          { key: 'overview' as const, label: 'Vue d\'ensemble', svg: <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/></svg> },
          ...(hasGoogle ? [{ key: 'google' as const, label: 'Google Business', svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 11h8.533c.044.385.067.78.067 1.184 0 2.734-.98 5.036-2.678 6.6C16.338 20.29 14.342 21 12 21c-4.97 0-9-4.03-9-9s4.03-9 9-9c2.43 0 4.468.895 6.048 2.352l-2.338 2.338C14.612 6.605 13.38 6 12 6c-3.315 0-6 2.685-6 6s2.685 6 6 6c2.202 0 3.927-1.078 4.845-2.748H12V11z"/></svg> }] : []),
          ...(hasFacebook ? [{ key: 'facebook' as const, label: 'Facebook', svg: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> }] : []),
          ...(hasInstagram ? [{ key: 'instagram' as const, label: 'Instagram', svg: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162S8.597 18.163 12 18.163s6.162-2.759 6.162-6.162S15.403 5.838 12 5.838zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> }] : []),
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all -mb-px ${
              activeTab === tab.key
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
            }`}>
            {tab.svg}<span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* === OVERVIEW TAB === */}
      {activeTab === 'overview' && (() => {
        const todayStr = new Date().toDateString();
        const todayPosts = upcomingPosts.filter(p => new Date(p.scheduled_for).toDateString() === todayStr);
        const nextPosts = upcomingPosts.filter(p => new Date(p.scheduled_for).toDateString() !== todayStr);
        const totalAudience = (fbInsights.insights?.totalFans || 0) + (igInsights.followers || 0);
        const avgRating = googleReviews.length > 0
          ? (googleReviews.reduce((s, r) => s + (STAR_MAP[r.starRating || ''] || 0), 0) / googleReviews.length).toFixed(1)
          : null;
        return (<>

        {/* ── KPIs prioritaires ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Audience totale"
            value={totalAudience}
            sub={[fbInsights.insights?.totalFans ? `${fmt(fbInsights.insights.totalFans)} abonnés FB` : '', igInsights.followers ? `${fmt(igInsights.followers)} abonnés IG` : ''].filter(Boolean).join(' · ') || 'Connectez vos réseaux'}
            trend={null}
            color="violet"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>}
          />
          <KpiCard
            label="Posts ce mois"
            value={stats.postsThisMonth}
            sub={`${stats.postsLastMonth} le mois dernier`}
            trend={stats.monthGrowth}
            color="blue"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>}
          />
          <KpiCard
            label="Posts programmés"
            value={upcomingPosts.length}
            sub={todayPosts.length > 0 ? `${todayPosts.length} aujourd'hui` : 'Aucun pour aujourd\'hui'}
            trend={null}
            color="amber"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
          />
          <KpiCard
            label="Engagement total"
            value={engagement.totalLikes + engagement.totalComments + engagement.totalShares}
            sub={avgRating ? `Note Google : ${avgRating}/5 ★` : `${fmt(engagement.totalViews)} vues`}
            trend={null}
            color="pink"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>}
          />
        </div>

        {/* ── Planning ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Colonne principale : planning */}
          <div className="lg:col-span-2 space-y-4">

            {/* Aujourd'hui */}
            <div className="rounded-2xl overflow-hidden border border-violet-200 shadow-sm">
              <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-3 flex items-center justify-between">
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  Publications d'aujourd'hui
                </h3>
                <span className="text-white/70 text-xs">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
              </div>
              <div className="bg-white p-4">
                {todayPosts.length === 0 ? (
                  <div className="flex items-center gap-3 py-3 text-gray-400">
                    <span className="text-2xl">📅</span>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Rien de prévu aujourd'hui</p>
                      <p className="text-xs text-gray-400">Planifiez un post depuis le Calendrier</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {todayPosts.map(post => (
                      <div key={post.id} className="flex items-start gap-3 p-3 rounded-xl bg-violet-50 border border-violet-100">
                        <div className="shrink-0 text-center bg-white border border-violet-200 rounded-lg px-2 py-1 min-w-[52px]">
                          <p className="text-[11px] font-bold text-violet-700">{new Date(post.scheduled_for).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800 line-clamp-2 font-medium">{post.content}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            {post.platforms.map(p => <span key={p} className="text-sm" title={PLATFORM_CONFIG[p]?.name}>{PLATFORM_CONFIG[p]?.icon || '🔗'}</span>)}
                          </div>
                        </div>
                        <span className="shrink-0 text-[10px] font-semibold bg-violet-600 text-white px-2 py-0.5 rounded-full">Programmé</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Prochains posts */}
            {nextPosts.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-100 px-5 py-3">
                  <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-gray-500"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    À venir
                    <span className="ml-auto bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full">{nextPosts.length}</span>
                  </h3>
                </div>
                <div className="divide-y divide-gray-50">
                  {nextPosts.slice(0, 4).map(post => (
                    <div key={post.id} className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                      <div className="shrink-0 text-center bg-blue-50 rounded-lg px-2 py-1 min-w-[60px]">
                        <p className="text-[10px] font-bold text-blue-700">{new Date(post.scheduled_for).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</p>
                        <p className="text-[10px] text-blue-500">{new Date(post.scheduled_for).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 line-clamp-1">{post.content}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          {post.platforms.map(p => <span key={p} className="text-xs">{PLATFORM_CONFIG[p]?.icon || '🔗'}</span>)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Colonne droite : comptes */}
          <div className="space-y-4">

            {/* Comptes connectés */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 flex items-center justify-between">
                <h3 className="font-semibold text-white text-sm">Comptes</h3>
                <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{accounts.length}</span>
              </div>
              <div className="p-3">
                {accounts.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">Aucun réseau connecté</p>
                ) : (
                  <div className="space-y-2">
                    {accounts.map(acc => {
                      const cfg = PLATFORM_CONFIG[acc.platform] || { name: acc.platform, icon: '🔗', color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200' };
                      const isOk = acc.sync_status === 'connected';
                      // Get audience for this platform
                      const audience = acc.platform === 'facebook' ? fbInsights.insights?.totalFans
                        : acc.platform === 'instagram' ? igInsights.followers
                        : null;
                      return (
                        <div key={acc.id} className={`flex items-center gap-2.5 p-2.5 rounded-xl ${cfg.bg} border ${cfg.border}`}>
                          <span className="text-lg">{cfg.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`font-semibold text-xs truncate ${cfg.color}`}>{acc.platform_name || acc.platform_username || cfg.name}</p>
                            {audience ? (
                              <p className="text-[10px] text-gray-500 font-medium">{fmt(audience)} abonnés</p>
                            ) : (
                              <p className="text-[10px] text-gray-400 truncate">{acc.platform_username ? `@${acc.platform_username}` : cfg.name}</p>
                            )}
                          </div>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${isOk ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        </div>
                      );
                    })}
                  </div>
                )}
                {Object.keys(platformBreakdown).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    {Object.entries(platformBreakdown).map(([platform, pb]) => {
                      const cfg = PLATFORM_CONFIG[platform] || { name: platform, icon: '🔗' };
                      const total = pb.published + pb.scheduled;
                      const maxTotal = Math.max(...Object.values(platformBreakdown).map(p => p.published + p.scheduled), 1);
                      return (
                        <div key={platform} className="flex items-center gap-2 text-xs">
                          <span className="text-sm w-4">{cfg.icon}</span>
                          <span className="text-gray-600 w-16 truncate text-[11px]">{cfg.name}</span>
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all" style={{ width: `${Math.round((total / maxTotal) * 100)}%` }} />
                          </div>
                          <span className="text-gray-500 font-semibold text-[11px] w-4 text-right">{total}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* ── Meilleures performances (full width) ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-3 flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white"><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>
            <h3 className="font-bold text-white text-sm">Meilleures performances</h3>
            <span className="ml-auto text-white/60 text-xs">Classé par engagement</span>
          </div>
          {topPosts.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <span className="text-4xl block mb-2">📭</span>
              <p className="text-sm">Pas encore de données d'engagement</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {topPosts.slice(0, 4).map((post, idx) => {
                const eng = post.engagement_data || {};
                let likes = 0, comments = 0, shares = 0;
                Object.values(eng).forEach((pe: any) => {
                  likes += Number(pe?.likes || pe?.reactions || 0);
                  comments += Number(pe?.comments || 0);
                  shares += Number(pe?.shares || pe?.reposts || 0);
                });
                const total = likes + comments + shares;
                const maxTotal = Math.max(1, (() => {
                  let m = 0;
                  topPosts.slice(0, 4).forEach(p => {
                    let t = 0;
                    Object.values(p.engagement_data || {}).forEach((pe: any) => {
                      t += Number(pe?.likes || pe?.reactions || 0) + Number(pe?.comments || 0) + Number(pe?.shares || pe?.reposts || 0);
                    });
                    m = Math.max(m, t);
                  });
                  return m;
                })());
                const rankColors = [
                  'from-amber-400 to-yellow-500',
                  'from-slate-400 to-gray-500',
                  'from-orange-400 to-amber-500',
                  'from-violet-400 to-indigo-500',
                ];
                const rankLabels = ['#1', '#2', '#3', '#4'];
                const thumb = post.media_urls?.[0];
                return (
                  <div key={post.id} className="flex items-center gap-4 px-5 py-4 hover:bg-amber-50/40 transition-colors group">
                    {/* Rang */}
                    <div className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${rankColors[idx]} flex items-center justify-center text-white font-black text-sm shadow-sm`}>
                      {rankLabels[idx]}
                    </div>
                    {/* Miniature */}
                    {thumb ? (
                      <img src={thumb} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0 border border-gray-100" />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-gray-100 shrink-0 flex items-center justify-center text-gray-300">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                      </div>
                    )}
                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug">{post.content}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] text-gray-400">{timeAgo(post.created_at)}</span>
                        {(post.target_platforms || []).map(p => (
                          <span key={p} className="text-xs">{PLATFORM_CONFIG[p]?.icon || '🔗'}</span>
                        ))}
                      </div>
                      {/* Barre de score */}
                      <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden w-full max-w-[160px]">
                        <div className={`h-full bg-gradient-to-r ${rankColors[idx]} rounded-full`} style={{ width: `${Math.round((total / maxTotal) * 100)}%` }} />
                      </div>
                    </div>
                    {/* Stats */}
                    <div className="shrink-0 flex gap-3">
                      <div className="text-center">
                        <p className="text-base font-black text-rose-500">{fmt(likes)}</p>
                        <p className="text-[9px] text-gray-400 font-medium">Likes</p>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-black text-blue-500">{fmt(comments)}</p>
                        <p className="text-[9px] text-gray-400 font-medium">Comm.</p>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-black text-emerald-500">{fmt(shares)}</p>
                        <p className="text-[9px] text-gray-400 font-medium">Partages</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Derniers posts ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-700 to-slate-600 px-5 py-3 flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            <h3 className="font-bold text-white text-sm">Derniers posts</h3>
            <span className="ml-auto text-white/50 text-xs">{recentPosts.length} post{recentPosts.length > 1 ? 's' : ''}</span>
          </div>
          {recentPosts.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <span className="text-4xl block mb-2">📝</span>
              <p className="text-sm">Aucun post créé pour le moment</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentPosts.slice(0, 6).map(post => {
                const statusCfg = STATUS_BADGES[post.status] || { label: post.status, class: 'bg-gray-100 text-gray-600' };
                const borderColor: Record<string, string> = {
                  published: 'border-l-emerald-400',
                  scheduled: 'border-l-blue-400',
                  draft: 'border-l-gray-300',
                  failed: 'border-l-red-400',
                  publishing: 'border-l-yellow-400',
                };
                const eng = post.engagement_data || {};
                let likes = 0, comments = 0;
                Object.values(eng).forEach((pe: any) => {
                  likes += Number(pe?.likes || pe?.reactions || 0);
                  comments += Number(pe?.comments || 0);
                });
                const thumb = post.media_urls?.[0];
                return (
                  <div key={post.id} className={`flex items-center gap-4 px-5 py-3.5 border-l-4 ${borderColor[post.status] || 'border-l-gray-200'} hover:bg-gray-50 transition-colors cursor-pointer`} onClick={() => setSelectedPost(post)}>
                    {thumb ? (
                      <img src={thumb} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0 border border-gray-100" />
                    ) : (
                      <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 shrink-0 flex items-center justify-center">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-4 h-4 text-gray-400"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium line-clamp-1">{post.content}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {(post.platforms || []).map(p => {
                          const cfg = PLATFORM_CONFIG[p];
                          return cfg ? (
                            <span key={p} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                              {cfg.icon} {cfg.name}
                            </span>
                          ) : <span key={p} className="text-sm">🔗</span>;
                        })}
                        <span className="text-[10px] text-gray-400">{timeAgo(post.created_at)}</span>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-3">
                      {(likes > 0 || comments > 0) && (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          {likes > 0 && <span className="flex items-center gap-0.5 text-rose-400 font-semibold"><svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5"><path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"/></svg>{fmt(likes)}</span>}
                          {comments > 0 && <span className="flex items-center gap-0.5 text-blue-400 font-semibold"><svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5"><path d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7z"/></svg>{fmt(comments)}</span>}
                        </div>
                      )}
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusCfg.class}`}>{statusCfg.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Hashtags ── */}
        {topHashtags.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-3 flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4 text-white"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
              <h3 className="font-bold text-white text-sm">Hashtags les plus utilisés</h3>
            </div>
            <div className="p-5">
              <div className="flex flex-wrap gap-2.5">
                {topHashtags.map(({ tag, count }, i) => {
                  const maxCount = topHashtags[0]?.count || 1;
                  const size = i === 0 ? 'text-base px-4 py-1.5' : i < 3 ? 'text-sm px-3.5 py-1.5' : 'text-xs px-3 py-1';
                  const style = i === 0
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md'
                    : i === 1
                    ? 'bg-gradient-to-r from-violet-100 to-indigo-100 text-violet-800 border border-violet-200'
                    : i === 2
                    ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200'
                    : i < 7
                    ? 'bg-gray-100 text-gray-700 border border-gray-200'
                    : 'bg-gray-50 text-gray-500 border border-gray-100';
                  const barWidth = Math.max(10, Math.round((count / maxCount) * 100));
                  return (
                    <div key={tag} className={`group relative inline-flex items-center gap-1.5 rounded-full font-semibold cursor-default transition-all hover:scale-105 ${size} ${style}`}>
                      <span>#</span><span>{tag}</span>
                      <span className={`font-normal opacity-60 text-[10px]`}>{count}×</span>
                      {/* Tooltip barre */}
                      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-20 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <span className="block bg-gray-900 text-white text-[10px] rounded px-2 py-1 text-center mb-1">{count} post{count > 1 ? 's' : ''}</span>
                        <span className="block h-1 bg-gray-200 rounded-full overflow-hidden">
                          <span className="block h-full bg-violet-500 rounded-full" style={{ width: `${barWidth}%` }} />
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        </>);
      })()}

      {/* === GOOGLE TAB === */}
      {activeTab === 'google' && (<>

        {/* KPIs Google */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white"><path d="M12 11h8.533c.044.385.067.78.067 1.184 0 2.734-.98 5.036-2.678 6.6C16.338 20.29 14.342 21 12 21c-4.97 0-9-4.03-9-9s4.03-9 9-9c2.43 0 4.468.895 6.048 2.352l-2.338 2.338C14.612 6.605 13.38 6 12 6c-3.315 0-6 2.685-6 6s2.685 6 6 6c2.202 0 3.927-1.078 4.845-2.748H12V11z"/></svg>
              <h3 className="font-bold text-white text-sm">Performance Google Business</h3>
            </div>
            <div className="flex gap-1">
              {(() => {
                const months: { key: string; label: string }[] = [];
                for (let i = 5; i >= 0; i--) {
                  const d = new Date(); d.setMonth(d.getMonth() - i);
                  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  const label = MOIS_FR[d.getMonth()].replace('.', '') + (d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : '');
                  months.push({ key, label: label.charAt(0).toUpperCase() + label.slice(1) });
                }
                return months.map(m => (
                  <button key={m.key} onClick={() => setGoogleMonth(m.key)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${googleMonth === m.key ? 'bg-white text-emerald-700 shadow-sm' : 'bg-white/20 text-white hover:bg-white/30'}`}>{m.label}</button>
                ));
              })()}
            </div>
          </div>
          {!googleInsights ? (
            <div className="text-center py-10 text-gray-400">
              <p className="text-sm">Aucune donnée disponible pour ce mois.</p>
            </div>
          ) : (
            <div className="p-5">
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                {[
                  { label: 'Vues Recherche', value: googleInsights.viewsSearch, grad: 'from-blue-500 to-blue-600', icon: '🔍' },
                  { label: 'Vues Maps', value: googleInsights.viewsMaps, grad: 'from-emerald-500 to-teal-600', icon: '🗺️' },
                  { label: 'Clics site', value: googleInsights.actionsWebsite, grad: 'from-violet-500 to-indigo-600', icon: '🖱️' },
                  { label: 'Appels', value: googleInsights.actionsPhone, grad: 'from-amber-500 to-orange-500', icon: '📞' },
                  { label: 'Itinéraires', value: googleInsights.actionsDrivingDirections, grad: 'from-rose-500 to-pink-600', icon: '📍' },
                  { label: 'Total vues', value: googleInsights.viewsSearch + googleInsights.viewsMaps, grad: 'from-slate-600 to-slate-700', icon: '👁️' },
                ].map(s => (
                  <div key={s.label} className={`rounded-2xl bg-gradient-to-br ${s.grad} p-4 text-white text-center shadow-sm`}>
                    <p className="text-xl mb-1">{s.icon}</p>
                    <p className="text-2xl font-black">{fmt(s.value)}</p>
                    <p className="text-[10px] text-white/70 font-medium mt-0.5 leading-tight">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Clics site web</p>
                  <LineChart data={googleClicksCurve} color="#10b981" />
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Vues recherche</p>
                  <LineChart data={googleViewsCurve} color="#3b82f6" />
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Appels</p>
                  <LineChart data={googleCallsCurve} color="#f59e0b" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Avis Google */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <button onClick={() => setShowAllReviews(!showAllReviews)} className="w-full flex items-center justify-between px-5 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 transition-colors">
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white"><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>
              <h3 className="font-bold text-white text-sm">Avis Google <span className="font-normal opacity-80">({googleReviews.length})</span></h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="bg-white/25 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full">{googleReviews.filter(r => STAR_MAP[r.starRating || ''] === 5).length} × 5★</span>
              <span className="bg-white/25 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full">Moy. {googleReviews.length > 0 ? (googleReviews.reduce((sum, r) => sum + (STAR_MAP[r.starRating || ''] || 0), 0) / googleReviews.length).toFixed(1) : '—'}★</span>
              <svg viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 text-white transition-transform ${showAllReviews ? 'rotate-180' : ''}`}><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </div>
          </button>
          {showAllReviews && (
            <div className="divide-y divide-gray-50">
              {googleReviews.length === 0 ? (
                <div className="text-center py-10 text-gray-400"><p className="text-sm">Aucun avis Google récupéré</p></div>
              ) : googleReviews.map((review, idx) => {
                const reviewId = review.reviewId || review.name?.split('/').pop() || `review-${idx}`;
                const starNum = STAR_MAP[review.starRating || ''] || 0;
                const isFiveStar = starNum === 5;
                const starColor = isFiveStar ? 'text-amber-400' : starNum >= 4 ? 'text-amber-300' : starNum >= 3 ? 'text-orange-400' : 'text-red-400';
                return (
                  <div key={reviewId} className={`px-5 py-4 border-l-4 hover:bg-gray-50 transition-colors ${isFiveStar ? 'border-l-amber-400 bg-amber-50/30' : starNum <= 2 ? 'border-l-red-300' : 'border-l-gray-200'}`}>
                    <div className="flex items-start gap-3">
                      {review.reviewer?.profilePhotoUrl
                        ? <img src={review.reviewer.profilePhotoUrl} alt="" className="w-10 h-10 rounded-full shrink-0 border border-gray-100" />
                        : <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-sm font-bold shrink-0">{(review.reviewer?.displayName || '?')[0]}</div>
                      }
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-sm text-gray-900">{review.reviewer?.displayName || 'Anonyme'}</span>
                          <span className={`text-sm font-medium ${starColor}`}>{stars(review.starRating || '')}</span>
                          {review.createTime && <span className="text-xs text-gray-400">{timeAgo(review.createTime)}</span>}
                          {isFiveStar && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">5 étoiles ✨</span>}
                        </div>
                        {review.comment && <p className="text-sm text-gray-700 mb-2 leading-relaxed">{cleanComment(review.comment)}</p>}
                        {review.reviewReply?.comment && (
                          <div className="ml-2 pl-3 border-l-2 border-emerald-300 bg-emerald-50 rounded-r-lg p-2.5 mb-2">
                            <p className="text-[10px] font-bold text-emerald-700 mb-0.5 uppercase tracking-wide">Votre réponse</p>
                            <p className="text-xs text-emerald-800">{review.reviewReply.comment}</p>
                          </div>
                        )}
                        <div className="flex gap-2 mt-2">
                          {!review.reviewReply?.comment && (
                            <button onClick={() => setReplyingTo(replyingTo === reviewId ? null : reviewId)}
                              className="text-xs px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 font-medium transition-colors">
                              Répondre
                            </button>
                          )}
                          {isFiveStar && review.comment && (
                            <button onClick={() => handleAutoPublish(review)} disabled={publishing === reviewId}
                              className="text-xs px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 font-medium transition-colors disabled:opacity-50">
                              {publishing === reviewId ? '⏳ Publication…' : '🚀 Publier sur mes réseaux'}
                            </button>
                          )}
                        </div>
                        {replyingTo === reviewId && (
                          <div className="mt-3 flex gap-2">
                            <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Votre réponse…"
                              className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                            <button onClick={() => handleReply(reviewId, review.accountId || 0)}
                              className="text-sm px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-medium transition-colors">Envoyer</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mots-clés */}
        {googleInsights?.searchKeywords && googleInsights.searchKeywords.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button onClick={() => setShowAllKeywords(!showAllKeywords)} className="w-full flex items-center justify-between px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-colors">
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <h3 className="font-bold text-white text-sm">Mots-clés de recherche <span className="font-normal opacity-80">({googleInsights.searchKeywords.length})</span></h3>
              </div>
              <svg viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 text-white transition-transform ${showAllKeywords ? 'rotate-180' : ''}`}><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </button>
            {showAllKeywords && (
              <div className="p-5 space-y-3">
                {googleInsights.searchKeywords.map((kw, i) => {
                  const maxImpressions = googleInsights.searchKeywords![0].impressions || 1;
                  const pct = Math.round((kw.impressions / maxImpressions) * 100);
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black shrink-0 ${i === 0 ? 'bg-blue-600 text-white' : i < 3 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-800 font-medium truncate">{kw.keyword}</span>
                          <span className="text-xs text-gray-400 ml-2 shrink-0 font-semibold">{kw.impressions} imp.</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </>)}

      {/* === FACEBOOK TAB === */}
      {activeTab === 'facebook' && (<>

        {/* KPIs Facebook */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#1877f2] to-[#0d6ef0] px-5 py-3 flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            <h3 className="font-bold text-white text-sm">
              Performance Facebook
              {fbInsights.pageName && <span className="font-normal opacity-70 ml-1">— {fbInsights.pageName}</span>}
            </h3>
          </div>
          {!fbInsights.insights ? (
            <div className="text-center py-10 text-gray-400">
              <p className="text-sm">Connectez une page Facebook pour voir ses statistiques.</p>
            </div>
          ) : (
            <div className="p-5">
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: "J'aime la page", value: fbInsights.insights.totalFans, grad: 'from-[#1877f2] to-[#0d6ef0]', icon: '👍' },
                  { label: 'Abonnés', value: fbInsights.insights.totalFollowers, grad: 'from-indigo-500 to-violet-600', icon: '👥' },
                  { label: 'Visiteurs uniques', value: fbInsights.insights.uniqueVisitors, grad: 'from-sky-500 to-blue-600', icon: '👁️' },
                  { label: 'Engagements', value: fbInsights.insights.postEngagements, grad: 'from-emerald-500 to-teal-600', icon: '📊' },
                  { label: 'Vues page', value: fbInsights.insights.pageViews, grad: 'from-violet-500 to-purple-600', icon: '🔗' },
                  { label: 'Réactions', value: fbInsights.insights.reactions, grad: 'from-rose-500 to-pink-600', icon: '❤️' },
                ].map(s => (
                  <div key={s.label} className={`rounded-2xl bg-gradient-to-br ${s.grad} p-4 text-white text-center shadow-sm`}>
                    <p className="text-xl mb-1">{s.icon}</p>
                    <p className="text-2xl font-black">{fmt(s.value)}</p>
                    <p className="text-[10px] text-white/70 font-medium mt-0.5 leading-tight">{s.label}</p>
                  </div>
                ))}
              </div>
              {(fbImpressionsCurve.length > 1 || fbEngagementCurve.length > 1) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
                  {fbImpressionsCurve.length > 1 && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Impressions (30j)</p>
                      <LineChart data={fbImpressionsCurve} color="#1877f2" />
                    </div>
                  )}
                  {fbEngagementCurve.length > 1 && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Engagements (30j)</p>
                      <LineChart data={fbEngagementCurve} color="#db2777" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Publications Facebook */}
        {fbInsights.posts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-slate-700 to-slate-600 px-5 py-3 flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
              <h3 className="font-bold text-white text-sm">Dernières publications</h3>
              <span className="ml-auto text-white/50 text-xs">{fbInsights.posts.length} post{fbInsights.posts.length > 1 ? 's' : ''}</span>
            </div>
            <div className="divide-y divide-gray-50">
              {fbInsights.posts.slice(0, 5).map((post: any) => (
                <div key={post.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors">
                  {post.full_picture ? (
                    <img src={post.full_picture} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0 border border-gray-100" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 shrink-0 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#1877f2" strokeWidth={1.5} className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug">{post.message || <span className="italic text-gray-400">Publication sans texte</span>}</p>
                    <span className="text-[10px] text-gray-400 mt-1 block">{post.created_time ? timeAgo(post.created_time) : ''}</span>
                  </div>
                  <div className="shrink-0 flex gap-3">
                    <div className="text-center">
                      <p className="text-base font-black text-rose-500">{fmt(post.likes)}</p>
                      <p className="text-[9px] text-gray-400">Likes</p>
                    </div>
                    <div className="text-center">
                      <p className="text-base font-black text-blue-500">{fmt(post.comments)}</p>
                      <p className="text-[9px] text-gray-400">Comm.</p>
                    </div>
                    <div className="text-center">
                      <p className="text-base font-black text-emerald-500">{fmt(post.shares)}</p>
                      <p className="text-[9px] text-gray-400">Partages</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Commentaires Facebook */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#1877f2] to-indigo-600 px-5 py-3 flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white"><path d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7z"/></svg>
            <h3 className="font-bold text-white text-sm">Commentaires <span className="font-normal opacity-70">({fbComments.length})</span></h3>
          </div>
          {fbComments.length === 0 ? (
            <div className="text-center py-10 text-gray-400"><p className="text-sm">Aucun commentaire récent</p></div>
          ) : (
            <div className="divide-y divide-gray-50">
              {fbComments.map((c: any) => (
                <div key={c.commentId} className="flex items-start gap-3 px-5 py-4 border-l-4 border-l-blue-300 hover:bg-blue-50/30 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1877f2] to-indigo-500 flex items-center justify-center text-white text-sm font-bold shrink-0">{(c.from || '?')[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900">{c.from}</span>
                      <span className="text-[10px] text-gray-400">{c.createdTime ? timeAgo(c.createdTime) : ''}</span>
                      {c.likeCount > 0 && <span className="text-[10px] text-rose-400 font-semibold">❤️ {c.likeCount}</span>}
                    </div>
                    <p className="text-sm text-gray-700 mb-1">{c.message}</p>
                    <p className="text-[10px] text-gray-400 italic truncate mb-2">Sur : {c.postMessage || 'Publication'}</p>
                    <button onClick={() => setReplyingTo(replyingTo === c.commentId ? null : c.commentId)}
                      className="text-xs px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 font-medium transition-colors">
                      Répondre
                    </button>
                    {replyingTo === c.commentId && (
                      <div className="mt-2 flex gap-2">
                        <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Votre réponse…"
                          className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                        <button onClick={() => handleFbReply(c.commentId, c.accountId)}
                          className="text-sm px-4 py-2 bg-[#1877f2] text-white rounded-xl hover:bg-blue-700 font-medium transition-colors">Envoyer</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>)}

      {/* === INSTAGRAM TAB === */}
      {activeTab === 'instagram' && (<>

        {/* KPIs Instagram */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)' }}>
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162S8.597 18.163 12 18.163s6.162-2.759 6.162-6.162S15.403 5.838 12 5.838zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            <h3 className="font-bold text-white text-sm">
              Performance Instagram
              {igInsights.accountName && <span className="font-normal opacity-70 ml-1">— @{igInsights.accountName}</span>}
            </h3>
          </div>
          {!igInsights.insights ? (
            <div className="text-center py-10 text-gray-400">
              <p className="text-sm">Connectez un compte Instagram Business pour voir ses statistiques.</p>
            </div>
          ) : (
            <div className="p-5">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Abonnés', value: igInsights.followers, grad: 'from-violet-600 to-purple-700', icon: '👥' },
                  { label: 'Impressions', value: igInsights.insights.impressions, grad: 'from-pink-500 to-rose-600', icon: '👁️' },
                  { label: 'Portée', value: igInsights.insights.reach, grad: 'from-orange-500 to-amber-500', icon: '📡' },
                  { label: 'Visites profil', value: igInsights.insights.profileViews, grad: 'from-fuchsia-500 to-pink-600', icon: '👤' },
                ].map(s => (
                  <div key={s.label} className={`rounded-2xl bg-gradient-to-br ${s.grad} p-5 text-white text-center shadow-sm`}>
                    <p className="text-2xl mb-1">{s.icon}</p>
                    <p className="text-3xl font-black">{fmt(s.value)}</p>
                    <p className="text-[11px] text-white/70 font-medium mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              {(igImpressionsCurve.length > 1 || igReachCurve.length > 1) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
                  {igImpressionsCurve.length > 1 && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Impressions (30j)</p>
                      <LineChart data={igImpressionsCurve} color="#ec4899" />
                    </div>
                  )}
                  {igReachCurve.length > 1 && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Portée (30j)</p>
                      <LineChart data={igReachCurve} color="#8b5cf6" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Publications Instagram */}
        {igInsights.media.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #6b21a8 0%, #9d174d 100%)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              <h3 className="font-bold text-white text-sm">Dernières publications</h3>
              <span className="ml-auto text-white/50 text-xs">{igInsights.media.length} post{igInsights.media.length > 1 ? 's' : ''}</span>
            </div>
            <div className="divide-y divide-gray-50">
              {igInsights.media.slice(0, 5).map((m: any) => (
                <div key={m.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors">
                  {(m.thumbnail_url || m.media_url) ? (
                    <img src={m.thumbnail_url || m.media_url} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0 border border-gray-100" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl shrink-0 flex items-center justify-center text-2xl" style={{ background: 'linear-gradient(135deg, #833ab4, #fd1d1d)' }}>
                      {m.media_type === 'VIDEO' ? '🎬' : '🖼️'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {m.caption
                      ? <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug">{m.caption}</p>
                      : <p className="text-sm italic text-gray-400">Publication sans légende</p>
                    }
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-gray-400">{m.timestamp ? timeAgo(m.timestamp) : ''}</span>
                      {m.media_type === 'VIDEO' && <span className="text-[10px] font-semibold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Vidéo</span>}
                    </div>
                  </div>
                  <div className="shrink-0 flex gap-3">
                    <div className="text-center">
                      <p className="text-base font-black text-rose-500">{fmt(m.likes)}</p>
                      <p className="text-[9px] text-gray-400">Likes</p>
                    </div>
                    <div className="text-center">
                      <p className="text-base font-black text-blue-500">{fmt(m.comments)}</p>
                      <p className="text-[9px] text-gray-400">Comm.</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Commentaires Instagram */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)' }}>
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white"><path d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7z"/></svg>
            <h3 className="font-bold text-white text-sm">Commentaires <span className="font-normal opacity-70">({igComments.length})</span></h3>
          </div>
          {igComments.length === 0 ? (
            <div className="text-center py-10 text-gray-400"><p className="text-sm">Aucun commentaire récent</p></div>
          ) : (
            <div className="divide-y divide-gray-50">
              {igComments.map((c: any) => (
                <div key={c.commentId} className="flex items-start gap-3 px-5 py-4 border-l-4 border-l-pink-400 hover:bg-pink-50/30 transition-colors">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: 'linear-gradient(135deg, #833ab4, #fd1d1d)' }}>
                    {(c.username || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900">@{c.username}</span>
                      <span className="text-[10px] text-gray-400">{c.createdTime ? timeAgo(c.createdTime) : ''}</span>
                      {c.likeCount > 0 && <span className="text-[10px] text-rose-400 font-semibold">❤️ {c.likeCount}</span>}
                    </div>
                    <p className="text-sm text-gray-700 mb-1">{c.text}</p>
                    <p className="text-[10px] text-gray-400 italic truncate mb-2">Sur : {c.mediaCaption || 'Publication'}</p>
                    <button onClick={() => setReplyingTo(replyingTo === c.commentId ? null : c.commentId)}
                      className="text-xs px-3 py-1.5 rounded-full bg-pink-50 border border-pink-200 text-pink-700 hover:bg-pink-100 font-medium transition-colors">
                      Répondre
                    </button>
                    {replyingTo === c.commentId && (
                      <div className="mt-2 flex gap-2">
                        <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Votre réponse…"
                          className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-400" />
                        <button onClick={() => handleIgReply(c.commentId, c.accountId)}
                          className="text-sm px-4 py-2 text-white rounded-xl font-medium transition-colors" style={{ background: 'linear-gradient(135deg, #833ab4, #fd1d1d)' }}>Envoyer</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>)}
      {/* Post Preview Modal */}
      {selectedPost && (() => {
        const platforms = ('platforms' in selectedPost ? selectedPost.platforms : (selectedPost as any).target_platforms) || [];
        const canEdit = ['draft', 'scheduled', 'failed'].includes(selectedPost.status);
        const token = localStorage.getItem('authToken');

        const closeModal = () => {
          setSelectedPost(null);
          setEditMode(false);
          setEditContent('');
          setEditError(null);
          setDeleteConfirm(false);
        };

        const handleSave = async () => {
          if (!editContent.trim()) return;
          setEditSaving(true);
          setEditError(null);
          try {
            const res = await fetch(`/api/posts/${(selectedPost as any).id}`, {
              method: 'PUT',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ content: editContent }),
            });
            if (!res.ok) throw new Error((await res.json()).message || 'Erreur');
            // Update in-place
            (selectedPost as any).content = editContent;
            setEditMode(false);
            // Refresh data
            const r2 = await fetch('/api/analytics/overview', { headers: { Authorization: `Bearer ${token}` } });
            if (r2.ok) setData(await r2.json());
          } catch (e: any) {
            setEditError(e.message);
          } finally {
            setEditSaving(false);
          }
        };

        const handleDelete = async () => {
          try {
            const res = await fetch(`/api/posts/${(selectedPost as any).id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error((await res.json()).message || 'Erreur');
            closeModal();
            const r2 = await fetch('/api/analytics/overview', { headers: { Authorization: `Bearer ${token}` } });
            if (r2.ok) setData(await r2.json());
          } catch (e: any) {
            setEditError(e.message);
          }
        };

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={closeModal}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2 flex-wrap">
                  {platforms.map((p: string) => {
                    const cfg = PLATFORM_CONFIG[p];
                    return cfg ? (
                      <span key={p} className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                        {cfg.icon} {cfg.name}
                      </span>
                    ) : <span key={p} className="text-lg">🔗</span>;
                  })}
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${(STATUS_BADGES[selectedPost.status] || { class: 'bg-gray-100 text-gray-600' }).class}`}>
                    {(STATUS_BADGES[selectedPost.status] || { label: selectedPost.status }).label}
                  </span>
                </div>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors ml-2 shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>

              {/* Image */}
              {selectedPost.media_urls?.[0] && (
                <img src={selectedPost.media_urls[0]} alt="" className="w-full max-h-60 object-cover" />
              )}

              {/* Content */}
              <div className="p-5">
                {editMode ? (
                  <textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    className="w-full text-sm text-gray-800 border border-violet-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none leading-relaxed"
                    rows={6}
                    autoFocus
                  />
                ) : (
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{selectedPost.content}</p>
                )}
                {editError && <p className="text-xs text-red-500 mt-2">{editError}</p>}
                {'scheduled_for' in selectedPost && selectedPost.scheduled_for && (
                  <p className="text-xs text-blue-500 mt-3">Programmé : {new Date((selectedPost as any).scheduled_for).toLocaleString('fr-FR')}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">Créé : {new Date(selectedPost.created_at).toLocaleString('fr-FR')}</p>
              </div>

              {/* Actions — seulement si pas encore publié */}
              {canEdit && (
                <div className="px-5 pb-5 flex items-center gap-2">
                  {editMode ? (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={editSaving}
                        className="flex-1 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
                      >
                        {editSaving ? 'Enregistrement…' : '✓ Enregistrer'}
                      </button>
                      <button
                        onClick={() => { setEditMode(false); setEditError(null); }}
                        className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-xl transition-colors"
                      >
                        Annuler
                      </button>
                    </>
                  ) : deleteConfirm ? (
                    <>
                      <span className="text-sm text-red-600 font-medium flex-1">Supprimer ce post ?</span>
                      <button onClick={handleDelete} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-xl transition-colors">
                        Confirmer
                      </button>
                      <button onClick={() => setDeleteConfirm(false)} className="px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                        Annuler
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => { setEditMode(true); setEditContent(selectedPost.content); }}
                        className="flex-1 py-2 border border-violet-200 text-violet-700 hover:bg-violet-50 text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        Modifier
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(true)}
                        className="px-4 py-2 border border-red-200 text-red-500 hover:bg-red-50 text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                        Supprimer
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ==================== SUB-COMPONENTS ====================

function KpiCard({ label, value, sub, trend, color, icon }: {
  label: string; value: number; sub: string; trend: number | null; color: string; icon: React.ReactNode;
}) {
  const gradients: Record<string, string> = {
    violet: 'from-violet-600 to-purple-700',
    blue:   'from-blue-500 to-indigo-600',
    pink:   'from-pink-500 to-rose-600',
    amber:  'from-amber-400 to-orange-500',
    green:  'from-emerald-500 to-teal-600',
  };
  const grad = gradients[color] || gradients.violet;
  return (
    <div className={`bg-gradient-to-br ${grad} rounded-2xl p-5 text-white shadow-lg relative overflow-hidden`}>
      {/* Decorative circle */}
      <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10" />
      <div className="absolute -bottom-8 -right-2 w-20 h-20 rounded-full bg-white/5" />
      <div className="relative z-10 flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white">{icon}</div>
          {trend !== null && (
            <span className={`flex items-center gap-0.5 text-xs font-bold px-2.5 py-1 rounded-full ${trend >= 0 ? 'bg-white/25 text-white' : 'bg-red-900/40 text-red-100'}`}>
              {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
            </span>
          )}
        </div>
        <div>
          <p className="text-white/70 text-[11px] font-semibold uppercase tracking-widest mb-1">{label}</p>
          <p className="text-5xl font-black leading-none">{fmt(value)}</p>
          <p className="text-white/60 text-xs mt-2">{sub}</p>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) {
  const colors: Record<string, string> = {
    pink: 'bg-pink-50 border-pink-100',
    blue: 'bg-blue-50 border-blue-100',
    green: 'bg-green-50 border-green-100',
    purple: 'bg-purple-50 border-purple-100',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color] || colors.blue}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-base">{icon}</span>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className="text-xl font-bold text-gray-900">{fmt(value)}</p>
    </div>
  );
}

function SummaryCard({ label, value, icon, alert }: { label: string; value: number; icon: string; alert?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 text-center ${alert ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
      <span className="text-xl">{icon}</span>
      <p className={`text-2xl font-bold mt-1 ${alert ? 'text-red-700' : 'text-gray-900'}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}
